// src/components/DataTransferSection.jsx
// Import history for the workspace, in Profile & Settings → Data.
//
// WHAT THIS USED TO BE, AND WHY IT SHRANK
// P0.14 put the whole CSV feature here: an entity picker, export and template
// buttons, and the upload → preview → commit flow. P0.15 moved all of that onto
// each entity's own page, behind a ⇅ Data button, because the picker only existed
// to serve a centralised panel — a user on the Accounts page already knows which
// entity they mean.
//
// Keeping the controls in both places would be worse than either: two paths to
// the same action that must be kept in step by hand, and a user who learns one
// and never discovers the other.
//
// WHAT IS LEFT IS THE PART THAT HAS NO OTHER HOME.
// Import history is genuinely per-WORKSPACE, not per-entity — /api/data/import-runs
// returns every entity's runs, and "what did I import last week" is a question
// about the tenant. An entity page is the wrong place for it, and the error CSV of
// a past run would otherwise be unreachable once the success screen is dismissed.

import React, { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { downloadFile } from '../lib/download';
import './DataTransferSection.css';

export default function DataTransferSection() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/data/import-runs?limit=25');
      if (!res.ok) {
        setError('Import history could not be loaded.');
        return;
      }
      const body = await res.json();
      setRuns(body.runs || []);
      setError(null);
    } catch {
      setError('Import history could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const downloadErrors = async (runId) => {
    const out = await downloadFile(
      `/api/data/import-errors?runId=${encodeURIComponent(runId)}`, 'import-errors.csv');
    if (!out.ok) setError(out.error);
  };

  return (
    <div className="dt-root">
      {error && <div className="dt-message dt-message--error" role="status">{error}</div>}

      {loading && <p className="dt-empty">Loading…</p>}

      {!loading && runs.length === 0 && (
        <p className="dt-empty">
          No imports yet. Use <strong>⇅ Data</strong> on the Accounts, Contacts, Leads,
          Pipeline or Tasks page to import a CSV — every import will be listed here.
        </p>
      )}

      {!loading && runs.length > 0 && (
        <>
          <div className="dt-table-wrap">
            <table className="dt-table">
              <thead>
                <tr>
                  <th>When</th><th>Data set</th><th>File</th>
                  <th>Imported</th><th>Skipped</th><th>Problems</th><th />
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.entityLabel}</td>
                    <td className="dt-cell-value">{r.filename || '—'}</td>
                    <td>{r.inserted}</td>
                    <td>{r.skipped}</td>
                    <td>{r.errorCount}</td>
                    <td>
                      {r.errorCount > 0 && (
                        <button className="dt-link-btn" onClick={() => downloadErrors(r.id)}>
                          Errors CSV
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="dt-note">
            Skipped rows already existed and were not overwritten. Download the errors CSV
            to see exactly which rows were rejected and why.
          </p>
        </>
      )}
    </div>
  );
}
